import type { DoorLayer, LayerDocument, MapDocument, PropInstance } from "../document/types.ts";
import { anchoredIn, assertAreaRect, type AreaLeftReason, type AreaReach, type AreaRect } from "./area.ts";
import { doorSides, isGridLayer, paintedIn } from "./area-grids.ts";
import type { FrameSizeOf } from "./prop-hit.ts";

/**
 * What an area holds (ADR-128 decisions 5, 9, 10, 11 and 14): on each layer the reach takes, the cells, corners or
 * quarter cells painted inside the rectangle and the props and markers anchored there; the totals the bar's
 * "Takes" line reads; each layer the reach leaves that holds something there, with why; and what the rectangle's
 * edge cuts: props whose art crosses it, whether they go or stay, and doors painted on both sides of it.
 */

/**
 * A prop or a marker, by layer and id.
 */
export interface AreaThing {
  readonly layerId: string;
  readonly id: string;
}

/**
 * What one layer holds inside the rectangle: painted cells of its grid (tile cells, corners or quarter cells),
 * props and markers anchored there.
 */
export interface AreaLayerCount {
  readonly layerId: string;
  readonly kind: LayerDocument["kind"];
  readonly cells: number;
  readonly props: number;
  readonly markers: number;
}

/**
 * A layer the reach leaves that holds something inside the rectangle, and why it stays (decision 5).
 */
export interface AreaStaying extends AreaLayerCount {
  readonly why: AreaLeftReason;
}

/**
 * The "Takes" line: props, markers, painted tile cells, corners and quarter cells, each grid's count with the
 * number of taken layers that hold some.
 */
export interface AreaTotals {
  readonly props: number;
  readonly markers: number;
  readonly tiles: number;
  readonly tileLayers: number;
  readonly corners: number;
  readonly cornerLayers: number;
  readonly cells: number;
  readonly cellLayers: number;
}

/**
 * A prop on a taken layer whose extent crosses the rectangle's edge, and whether it goes (its anchor is inside) or
 * stays.
 */
export interface AreaCutProp extends AreaThing {
  readonly taken: boolean;
}

/**
 * What the edge cuts (decision 11): the props whose extent crosses it, how many of them go and stay, and the doors
 * of taken door layers painted both inside and outside it.
 */
export interface AreaCut {
  readonly props: readonly AreaCutProp[];
  readonly go: number;
  readonly stay: number;
  readonly doors: readonly { readonly layerId: string; readonly doorId: string }[];
}

/**
 * What an area holds, every list in the map's order and each layer's own order.
 */
export interface AreaContents {
  readonly taken: readonly AreaLayerCount[];
  readonly totals: AreaTotals;
  readonly props: readonly AreaThing[];
  readonly markers: readonly AreaThing[];
  readonly staying: readonly AreaStaying[];
  readonly cut: AreaCut;
}

/**
 * A box in tiles, fractional, left and top inclusive of the thing's first pixel and right and bottom past its last.
 */
export interface AreaExtent {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The extent a prop covers on the map, in tiles, or null when it has none to cut.
 */
export type PropExtentOf = (layerId: string, prop: PropInstance) => AreaExtent | null;

/**
 * The extent of a prop's art as the map draws it from its anchor: its frame's width to the right of its point and
 * its frame's height up from its anchor cell's bottom edge. A caller that also counts a footprint reaching past the
 * art passes its own extent.
 *
 * @param sizeOf The frame size lookup.
 * @param tileSize The world's tile size in pixels.
 * @returns The extent lookup.
 */
export function propArtExtent(sizeOf: FrameSizeOf, tileSize: number): PropExtentOf {
  return (_layerId, prop) => {
    const size = sizeOf(prop.frame.tileset, prop.frame.tile);
    const bottom = prop.y + 1;
    return { left: prop.x, top: bottom - size.h / tileSize, right: prop.x + size.w / tileSize, bottom };
  };
}

/**
 * Whether an extent crosses a rectangle's edge: it overlaps the rectangle and does not lie wholly inside it.
 *
 * @param extent The extent, in tiles.
 * @param rect The rectangle of tiles.
 * @returns True when it crosses.
 */
function crossesEdge(extent: AreaExtent, rect: AreaRect): boolean {
  const right = rect.x1 + 1;
  const bottom = rect.y1 + 1;
  const overlaps = extent.left < right && extent.right > rect.x0 && extent.top < bottom && extent.bottom > rect.y0;
  const inside = extent.left >= rect.x0 && extent.right <= right && extent.top >= rect.y0 && extent.bottom <= bottom;
  return overlaps && !inside;
}

/**
 * What one layer holds inside a rectangle.
 *
 * @param map The map.
 * @param layer The layer.
 * @param rect The rectangle of tiles.
 * @returns Its counts.
 */
function countOf(map: MapDocument, layer: LayerDocument, rect: AreaRect): AreaLayerCount {
  const cells = isGridLayer(layer) ? paintedIn(map, layer, rect) : 0;
  const props = layer.kind === "prop" ? layer.props.filter((p) => anchoredIn(rect, p.x, p.y)).length : 0;
  const markers = layer.kind === "entity" ? layer.entities.filter((e) => anchoredIn(rect, e.x, e.y)).length : 0;
  return { layerId: layer.id, kind: layer.kind, cells, props, markers };
}

/**
 * The doors of a door layer painted both inside and outside a rectangle, in the layer's list order.
 *
 * @param map The map.
 * @param layer The door layer.
 * @param rect The rectangle of tiles.
 * @returns The doors' ids.
 */
function cutDoors(map: MapDocument, layer: DoorLayer, rect: AreaRect): string[] {
  const sides = doorSides(map, layer, rect);
  return layer.doors.filter((_door, i) => sides[i]?.inside === true && sides[i]?.outside === true).map((door) => door.id);
}

/**
 * What an area holds on a map for a reach.
 *
 * @param map The map in view.
 * @param rect The rectangle of tiles.
 * @param reach The layers taken and left (`areaReach`).
 * @param extentOf Each prop's extent, which says whether the edge cuts it; without it no prop is counted as cut.
 * @returns The contents.
 * @throws RangeError when the rectangle is not one of whole tiles inside the map.
 */
export function areaContents(map: MapDocument, rect: AreaRect, reach: AreaReach, extentOf?: PropExtentOf): AreaContents {
  assertAreaRect(map, rect);
  const taken = new Set(reach.taken);
  const why = new Map(reach.left.map((left) => [left.layerId, left.why]));
  const counts: AreaLayerCount[] = [];
  const staying: AreaStaying[] = [];
  const props: AreaThing[] = [];
  const markers: AreaThing[] = [];
  const cutProps: AreaCutProp[] = [];
  const doors: { layerId: string; doorId: string }[] = [];
  const totals = { props: 0, markers: 0, tiles: 0, tileLayers: 0, corners: 0, cornerLayers: 0, cells: 0, cellLayers: 0 };
  for (const layer of map.layers) {
    const count = countOf(map, layer, rect);
    const left = why.get(layer.id);
    if (!taken.has(layer.id)) {
      if (left !== undefined && count.cells + count.props + count.markers > 0) {
        staying.push({ ...count, why: left });
      }
      continue;
    }
    counts.push(count);
    totals.props += count.props;
    totals.markers += count.markers;
    if (layer.kind === "tile") {
      totals.tiles += count.cells;
      totals.tileLayers += count.cells > 0 ? 1 : 0;
    } else if (layer.kind === "terrain") {
      totals.corners += count.cells;
      totals.cornerLayers += count.cells > 0 ? 1 : 0;
    } else {
      totals.cells += count.cells;
      totals.cellLayers += count.cells > 0 ? 1 : 0;
    }
    if (layer.kind === "prop") {
      for (const prop of layer.props) {
        const inside = anchoredIn(rect, prop.x, prop.y);
        if (inside) {
          props.push({ layerId: layer.id, id: prop.id });
        }
        const extent = extentOf?.(layer.id, prop) ?? null;
        if (extent !== null && crossesEdge(extent, rect)) {
          cutProps.push({ layerId: layer.id, id: prop.id, taken: inside });
        }
      }
    } else if (layer.kind === "entity") {
      markers.push(...layer.entities.filter((e) => anchoredIn(rect, e.x, e.y)).map((e) => ({ layerId: layer.id, id: e.id })));
    } else if (layer.kind === "door") {
      doors.push(...cutDoors(map, layer, rect).map((doorId) => ({ layerId: layer.id, doorId })));
    }
  }
  const go = cutProps.filter((p) => p.taken).length;
  return { taken: counts, totals, props, markers, staying, cut: { props: cutProps, go, stay: cutProps.length - go, doors } };
}
